<template>
  <div>
    <v-dialog
      v-model="showModal"
      fullscreen
      persistent
      transition="dialog-bottom-transition"
      scrollable
    >
      <v-card class="ptv-card">
        <!-- Toolbar -->
        <v-toolbar dark color="primary" dense class="ptv-toolbar">
          <v-btn icon dark @click="closeModal">
            <v-icon>mdi-close</v-icon>
          </v-btn>
          <v-toolbar-title>Vista de Planos y Entregas</v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn text @click="closeModal">Cerrar</v-btn>
        </v-toolbar>

        <div style="flex: 1; overflow: hidden; display: flex; flex-direction: column; min-height: 0;">
          <!-- Plano Selector -->
          <div class="px-4 pt-3 pb-2" style="flex-shrink: 0;">
            <v-select
              v-model="selectedPlano"
              :items="planos"
              item-text="title"
              item-value="id"
              label="Seleccione el Plano"
              prepend-icon="mdi-file-document-outline"
              outlined
              dense
              hide-details
              @change="loadPlanoEntregas"
            ></v-select>
          </div>

          <!-- Viewer area -->
          <div class="ptv-viewer-area" :class="{ 'ptv-viewer-area--mobile': isMobile }">
            <!-- Canvas / Forge viewer (izquierda) -->
            <div class="ptv-canvas">
              <v-btn
                v-if="isMobile && selectedPlano"
                class="ptv-sidebar-toggle"
                fab
                small
                dark
                color="primary"
                :title="sidebarOpen ? 'Ocultar entregas' : 'Mostrar entregas'"
                @click="sidebarOpen = !sidebarOpen"
              >
                <v-icon>mdi-layers-outline</v-icon>
              </v-btn>
              <div v-if="!selectedPlano" class="ptv-placeholder">
                <v-icon size="56" color="grey lighten-1">mdi-image-outline</v-icon>
                <div class="text-subtitle-2 mt-3 grey--text">Seleccione un plano para ver sus entregas</div>
              </div>
              <template v-else>
                <!-- Forge viewer -->
                <ForgePolygonViewer
                  v-if="selectedPlanoIsForge"
                  :key="selectedPlano"
                  :read-only="true"
                  :all-polygons="allPolygons"
                  :forge-file-urn="selectedForgeFileUrn"
                  style="height: 100%;"
                />
                <!-- Canvas viewer -->
                <ImagePolygonViewer
                  v-else
                  :plano="selectedPlanoObj"
                  :read-only="true"
                  :all-polygons="allPolygons"
                  style="height: 100%;"
                />
              </template>
            </div>

            <div
              v-if="isMobile && sidebarOpen"
              class="ptv-sidebar-backdrop"
              @click="sidebarOpen = false"
            ></div>

            <!-- Entregas sidebar (derecha) -->
            <div
              class="ptv-sidebar"
              :class="{ 'ptv-sidebar--open': !isMobile || sidebarOpen }"
            >
              <div class="px-3 py-2 d-flex align-center" style="border-bottom: 1px solid #f0f0f0; background: #fafafa; flex-shrink: 0;">
                <v-icon small color="primary" class="mr-2">mdi-package-variant-closed</v-icon>
                <span class="text-body-2 font-weight-medium">Entregas Asociadas</span>
                <v-spacer></v-spacer>
                <v-btn
                  v-if="isMobile"
                  icon
                  x-small
                  title="Cerrar entregas"
                  @click="sidebarOpen = false"
                >
                  <v-icon small>mdi-close</v-icon>
                </v-btn>
                <v-checkbox
                  v-if="entregasForSelectedPlano.length > 0"
                  :input-value="allEntregasChecked"
                  label="Todas"
                  dense
                  hide-details
                  class="mt-0 pt-0 mr-2"
                  @change="toggleAllEntregas"
                ></v-checkbox>
                <v-btn
                  v-if="entregasForSelectedPlano.length > 0"
                  icon
                  x-small
                  :title="allEntregasChecked ? 'Ocultar todas' : 'Mostrar todas'"
                  @click="toggleAllEntregas"
                >
                  <v-icon small>{{ allEntregasChecked ? 'mdi-eye-off-outline' : 'mdi-eye-outline' }}</v-icon>
                </v-btn>
              </div>

              <div style="flex: 1; overflow-y: auto;" class="pa-2">
                <div v-if="!selectedPlano" class="text-center pa-4 text-caption grey--text">
                  Seleccione un plano
                </div>
                <v-skeleton-loader v-else-if="loadingEntregas" type="list-item-two-line, list-item-two-line" />
                <template v-else-if="entregasForSelectedPlano.length > 0">
                  <v-list dense class="pa-0">
                    <v-list-item
                      v-for="(item, index) in entregasForSelectedPlano"
                      :key="item.id || index"
                      class="py-0 ptv-delivery-item"
                    >
                      <v-checkbox
                        v-model="visibleEntregaIds"
                        :value="item.id"
                        dense
                        hide-details
                        class="mt-0 pt-0 mr-1"
                      ></v-checkbox>
                      <v-list-item-avatar size="12" :color="item.color || '#2D4B95'" class="my-0 mr-2" style="min-width: 12px;"></v-list-item-avatar>
                      <v-list-item-content class="py-0">
                        <v-list-item-title class="text-caption font-weight-medium">{{ entregaLabel(item) }}</v-list-item-title>
                        <v-list-item-subtitle v-if="item.entrega && item.entrega.status_info" class="text-caption">
                          <v-chip x-small :style="{ backgroundColor: item.entrega.status_info.color, color: 'white' }">
                            {{ item.entrega.status_info.text }}
                          </v-chip>
                        </v-list-item-subtitle>
                      </v-list-item-content>
                      <v-list-item-action class="my-0 ml-1">
                        <v-btn
                          icon
                          x-small
                          color="primary"
                          :loading="loadingEntregaId === item.entrega_id"
                          :disabled="!item.entrega_id"
                          :title="`Ver entrega Nº${item.entrega_id}`"
                          @click.stop="openEntregaDetail(item)"
                        >
                          <v-icon small>mdi-eye-outline</v-icon>
                        </v-btn>
                      </v-list-item-action>
                    </v-list-item>
                  </v-list>
                </template>
                <div v-else class="text-center pa-4 text-caption grey--text">
                  No hay entregas asociadas a este plano
                </div>
              </div>
            </div>
          </div>
        </div>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { getPlanos, getPlano, getEntregaByID, getConfig } from '@/helpers/api/entrega'
import { getJobTypes } from '@/helpers/api/user'
import ForgePolygonViewer from './ForgePolygonViewer.vue'
import ImagePolygonViewer from './ImagePolygonViewer.vue'

function formatPolygonLabel (item) {
  const number = item && item.entrega ? item.entrega.number : item && item.number
  return number != null ? `Nº${number}` : 'Entrega'
}

function formatEntregaListLabel (item) {
  if (!item || !item.entrega) return 'Entrega'
  const title = item.entrega.title || item.entrega.protocolo_name || 'Entrega'
  return item.entrega.number != null ? `Nº${item.entrega.number}: ${title}` : title
}

export default {
  name: 'PlanoTotalViewer',

  components: {
    ForgePolygonViewer,
    ImagePolygonViewer
  },

  data () {
    return {
      showModal: false,
      planos: [],
      selectedPlano: null,
      loadingEntregas: false,
      entregasForSelectedPlano: [],
      visibleEntregaIds: [],
      sidebarOpen: false,
      loadingEntregaId: null
    }
  },

  computed: {
    modalState () {
      return this.$store?.entregaStore?.state?.planoTotalViewerModal || false
    },

    isMobile () {
      return !!this.$vuetify?.breakpoint?.smAndDown
    },

    /** Plano seleccionado como objeto */
    selectedPlanoObj () {
      return this.planos.find(p => p.id === this.selectedPlano) || null
    },

    /** El plano seleccionado es de tipo Forge */
    selectedPlanoIsForge () {
      const plano = this.planos.find(p => p.id === this.selectedPlano)
      return !!(plano && (
        (plano.file && plano.file.forge_file && plano.file.forge_file.urn) ||
        (plano.forge_file && plano.forge_file.urn)
      ))
    },

    /** URN del modelo Forge del plano seleccionado */
    selectedForgeFileUrn () {
      const plano = this.planos.find(p => p.id === this.selectedPlano)
      return plano?.file?.forge_file?.urn || plano?.forge_file?.urn || null
    },

    /** Polígonos unificados para ForgePolygonViewer (allPolygons) e ImagePolygonViewer (allPolygons) */
    allPolygons () {
      return this.entregasForSelectedPlano
        .filter(item => this.visibleEntregaIds.includes(item.id))
        .filter(item => item.nodes && item.nodes.length > 0)
        .map(item => ({
          id:      item.id,
          nodes:   item.nodes,
          color:   item.color   || '#2D4B95',
          opacity: item.opacity != null ? item.opacity : 0.3,
          label:   formatPolygonLabel(item)
        }))
    },

    allEntregasChecked () {
      return this.entregasForSelectedPlano.length > 0 &&
        this.entregasForSelectedPlano.every(item => this.visibleEntregaIds.includes(item.id))
    }
  },

  watch: {
    modalState: {
      immediate: true,
      handler (newVal) {
        this.showModal = newVal
        if (newVal) {
          this.$nextTick(() => this.loadPlanos())
        }
      }
    },

    entregasForSelectedPlano (items) {
      this.visibleEntregaIds = items.map(item => item.id)
    }
  },

  methods: {
    closeModal () {
      this.showModal = false
      this.sidebarOpen = false
      this.$store.entregaStore.commit('setPlanoTotalViewerModal', false)
    },

    async loadPlanos () {
      const projectId = this.$store?.projectsStore?.state?.selectedProject?.id
      if (!projectId) return
      try {
        const response = await getPlanos({ project_id: projectId, start: 0, limit: 100 })
        if (response && response.code === 200 && response.planos) {
          this.planos = response.planos
          const requestedPlanoId = this.$store?.entregaStore?.state?.planoTotalViewerPlanoId
          const requestedPlanoExists = this.planos.some(plano => plano.id === requestedPlanoId)
          if (this.planos.length > 0 && (!this.selectedPlano || !this.planos.some(plano => plano.id === this.selectedPlano))) {
            this.selectedPlano = requestedPlanoExists ? requestedPlanoId : this.planos[0].id
            this.loadPlanoEntregas(this.selectedPlano)
          }
        }
      } catch (e) {
        console.error('[PlanoTotalViewer] Error loading planos:', e)
      }
    },

    async loadPlanoEntregas (planoId) {
      if (!planoId) return
      this.loadingEntregas           = true
      this.entregasForSelectedPlano  = []
      this.sidebarOpen               = false
      try {
        const projectId = this.$store?.projectsStore?.state?.selectedProject?.id
        const response = await getPlano({ project_id: projectId, plano_id: planoId })
        if (response && Array.isArray(response.planos)) {
          this.entregasForSelectedPlano = response.planos
        }
      } catch (e) {
        console.error('[PlanoTotalViewer] Error loading entregas:', e)
      } finally {
        this.loadingEntregas = false
      }
    },

    async openEntregaDetail (item) {
      const entregaId = item && item.entrega_id
      if (!entregaId) return

      const projectId = this.$store?.projectsStore?.state?.selectedProject?.id
      this.loadingEntregaId = entregaId
      this.sidebarOpen = false
      this.$store.entregaStore.commit('setCurrentEntrega', { loading: true })

      try {
        const entregaResponse = await getEntregaByID({ entrega_id: entregaId })
        const [configResponse, jobTypesResponse] = await Promise.all([
          getConfig({ project_id: projectId }),
          getJobTypes({ project_id: projectId }),
        ])

        if (jobTypesResponse && jobTypesResponse.job_types) {
          this.$store.usersStore.commit('setJobTypes', jobTypesResponse.job_types)
        }
        if (configResponse && configResponse.config) {
          this.$store.entregaStore.commit('setConfig', configResponse.config)
        }

        if (entregaResponse && entregaResponse.code === 403) {
          this.$store.entregaStore.commit('setCurrentEntrega', null)
        } else if (entregaResponse && entregaResponse.code === 200 && entregaResponse.entrega) {
          this.$store.entregaStore.commit('setCurrentEntrega', entregaResponse.entrega)
        } else {
          this.$store.entregaStore.commit('setCurrentEntrega', { loading: false, error: true })
        }
      } catch (error) {
        console.error('[PlanoTotalViewer] Error loading entrega:', error)
        this.$store.entregaStore.commit('setCurrentEntrega', { loading: false, error: true })
      } finally {
        this.loadingEntregaId = null
      }
    },

    toggleAllEntregas (checked) {
      const nextState = typeof checked === 'boolean' ? checked : !this.allEntregasChecked
      this.visibleEntregaIds = nextState
        ? this.entregasForSelectedPlano.map(item => item.id)
        : []
    },

    entregaLabel (item) {
      return formatEntregaListLabel(item)
    },


  }
}
</script>

<style scoped>
.ptv-card {
  display: flex;
  flex-direction: column;
  height: 100vh;
  height: 100dvh;
  overflow: hidden;
}

.ptv-placeholder {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: #f5f5f5;
}

.ptv-toolbar {
  flex: 0 0 48px !important;
  height: 48px !important;
  min-height: 48px !important;
  max-height: 48px !important;
}

.ptv-toolbar :deep(.v-toolbar__content) {
  height: 48px !important;
  min-height: 48px !important;
}

.ptv-viewer-area {
  flex: 1;
  overflow: hidden;
  display: flex;
  min-height: 0;
  position: relative;
}

.ptv-canvas {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  position: relative;
}

.ptv-sidebar {
  width: 300px;
  flex: 0 0 300px;
  border-left: 1px solid #e0e0e0;
  background: #fff;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.ptv-sidebar-toggle {
  position: absolute;
  right: 12px;
  top: 12px;
  z-index: 10;
}

.ptv-sidebar-backdrop {
  position: absolute;
  inset: 0;
  z-index: 19;
  background: rgba(0, 0, 0, 0.28);
}

.ptv-delivery-item {
  min-height: 36px !important;
}

/* Debe coincidir con isMobile (Vuetify smAndDown < 960px) */
@media (max-width: 959px) {
  .ptv-toolbar .v-toolbar__title {
    max-width: 48vw;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .ptv-sidebar {
    position: absolute;
    z-index: 20;
    top: 0;
    right: 0;
    bottom: 0;
    width: min(300px, calc(100vw - 32px));
    max-width: calc(100vw - 32px);
    transform: translateX(100%);
    transition: transform 0.2s ease;
    box-shadow: -4px 0 16px rgba(0, 0, 0, 0.2);
  }

  .ptv-sidebar--open {
    transform: translateX(0);
  }
}
</style>
